import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  MessageCircle, Send, Search, Users, Circle,
  Hash, ArrowLeft, GraduationCap, Pencil, Trash2, X,
  Paperclip, FileText, Image as ImageIcon, Loader, Eye,
} from 'lucide-react';
import { UserAvatar } from '../ui';
import FileViewerModal from '../ui/FileViewerModal';
import { useSocket } from '../../contexts/SocketContext';
import { getCurrentUser } from '../../utils/auth';
import apiClient from '../../utils/apiClient';
import './BatchChat.scss';

// ── Helpers ──

function formatMessageTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function getDateLabel(ts) {
  const d = new Date(ts);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// ── BatchListSidebar ──

function BatchListSidebar({ batches, activeBatchId, onSelectBatch, searchQuery, onSearchChange }) {
  const filtered = batches.filter((b) =>
    b.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="batch-chat__sidebar">
      <div className="batch-chat__sidebar-header">
        <div className="batch-chat__sidebar-title">Batch Chats</div>
        <div className="batch-chat__sidebar-search">
          <Search className="search-icon" />
          <input
            placeholder="Search batches..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      <div className="batch-chat__sidebar-list">
        {filtered.map((batch) => (
          <div
            key={batch._id}
            role="button"
            tabIndex={0}
            className={`batch-chat__sidebar-item${batch._id === activeBatchId ? ' batch-chat__sidebar-item--active' : ''}`}
            onClick={() => onSelectBatch(batch._id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectBatch(batch._id); } }}
          >
            <div className="batch-chat__sidebar-icon">
              <Hash size={16} />
            </div>
            <div className="batch-chat__sidebar-info">
              <div className="batch-chat__sidebar-name">{batch.name}</div>
              <div className="batch-chat__sidebar-course">{batch.courseName}</div>
              {batch.lastMessage && (
                <div className="batch-chat__sidebar-last-msg">
                  <span className="sender">{batch.lastMessage.senderName?.split(' ')[0]}:</span>{' '}
                  {batch.lastMessage.message?.substring(0, 35)}
                  {batch.lastMessage.message?.length > 35 ? '...' : ''}
                </div>
              )}
              <div className="batch-chat__sidebar-meta">
                <span className="batch-chat__sidebar-stat">
                  <Users size={12} />
                  {batch.memberCount}
                </span>
                {batch.onlineCount > 0 && (
                  <span className="batch-chat__sidebar-stat batch-chat__sidebar-stat--online">
                    <Circle size={8} />
                    {batch.onlineCount} online
                  </span>
                )}
              </div>
            </div>
            {batch.lastMessage?.createdAt && (
              <span className="batch-chat__sidebar-time">
                {formatMessageTime(batch.lastMessage.createdAt)}
              </span>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="batch-chat__empty" style={{ padding: '2rem' }}>
            <p className="batch-chat__empty-text">No batches found</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Attachment helpers ──

function isImageMime(mimeType) {
  return mimeType?.startsWith('image/');
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentRenderer({ attachments, isOwn }) {
  const [urls, setUrls] = useState({});
  const [viewerFile, setViewerFile] = useState(null);

  useEffect(() => {
    if (!attachments?.length) return;
    let cancelled = false;
    const loadUrls = async () => {
      const result = {};
      for (const att of attachments) {
        try {
          const { data } = await apiClient.post('/chat/download-url', { key: att.fileKey });
          if (!cancelled) result[att.fileKey] = data.url;
        } catch { /* ignore */ }
      }
      if (!cancelled) setUrls(result);
    };
    loadUrls();
    return () => { cancelled = true; };
  }, [attachments]);

  if (!attachments?.length) return null;

  const openViewer = (att, url) => {
    if (url) setViewerFile({ fileName: att.fileName, fileUrl: url, fileType: att.mimeType });
  };

  return (
    <>
      <div className="batch-chat__attachments">
        {attachments.map((att, i) => {
          const url = urls[att.fileKey];
          if (isImageMime(att.mimeType)) {
            return (
              <div key={i} className="batch-chat__attachment-image">
                {url ? (
                  <button type="button" className="batch-chat__attachment-image-btn" onClick={() => openViewer(att, url)}>
                    <img src={url} alt={att.fileName} loading="lazy" />
                  </button>
                ) : (
                  <div className="batch-chat__attachment-loading"><Loader size={16} className="spin" /></div>
                )}
              </div>
            );
          }
          return (
            <div key={i} className={`batch-chat__attachment-file${isOwn ? ' batch-chat__attachment-file--own' : ''}`}>
              <FileText size={16} />
              <div className="batch-chat__attachment-file-info">
                <span className="batch-chat__attachment-file-name">{att.fileName}</span>
                {att.fileSize && <span className="batch-chat__attachment-file-size">{formatFileSize(att.fileSize)}</span>}
              </div>
              {url && (
                <button type="button" className="batch-chat__attachment-download" title="View" onClick={() => openViewer(att, url)}>
                  <Eye size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <FileViewerModal
        show={!!viewerFile}
        onHide={() => setViewerFile(null)}
        fileUrl={viewerFile?.fileUrl}
        fileName={viewerFile?.fileName}
        fileType={viewerFile?.fileType}
      />
    </>
  );
}

// ── MessageBubble ──

function MessageBubble({ msg, isOwn, previousSenderId, onEdit, onDelete }) {
  const sameSender = previousSenderId === msg.sender;

  const bubbleClass = msg.isDeleted
    ? 'batch-chat__msg-bubble batch-chat__msg-bubble--deleted'
    : isOwn
    ? 'batch-chat__msg-bubble batch-chat__msg-bubble--own'
    : msg.senderRole === 'instructor'
    ? 'batch-chat__msg-bubble batch-chat__msg-bubble--instructor'
    : 'batch-chat__msg-bubble batch-chat__msg-bubble--other';

  const hasAttachments = msg.attachments?.length > 0 && !msg.isDeleted;

  return (
    <div className={`batch-chat__msg${isOwn ? ' batch-chat__msg--own' : ''}${sameSender ? ' batch-chat__msg--grouped' : ''}`}>
      {!isOwn && !sameSender ? (
        <div className="batch-chat__msg-avatar">
          <UserAvatar name={msg.senderName} size="sm" />
        </div>
      ) : !isOwn ? (
        <div className="batch-chat__msg-avatar-spacer" />
      ) : null}

      <div className="batch-chat__msg-body">
        {!sameSender && !isOwn && (
          <div className="batch-chat__msg-sender">
            <span className={`batch-chat__msg-sender-name${msg.senderRole === 'instructor' ? ' batch-chat__msg-sender-name--instructor' : ''}`}>
              {msg.senderName}
            </span>
            {msg.senderRole === 'instructor' && (
              <span className="batch-chat__msg-sender-badge">
                <GraduationCap size={10} />
                Instructor
              </span>
            )}
            {(msg.senderRole === 'admin' || msg.senderRole === 'superadmin') && (
              <span className="batch-chat__msg-sender-badge">
                Admin
              </span>
            )}
          </div>
        )}
        <div className={bubbleClass}>
          {msg.isDeleted ? (
            <em>This message was deleted</em>
          ) : (
            <>
              {hasAttachments && (
                <AttachmentRenderer attachments={msg.attachments} isOwn={isOwn} />
              )}
              {msg.type === 'link' ? (
                <a href={msg.message} target="_blank" rel="noopener noreferrer">
                  {msg.message}
                </a>
              ) : msg.message ? (
                <span>{msg.message}</span>
              ) : null}
            </>
          )}
          {msg.isEdited && !msg.isDeleted && (
            <span className="batch-chat__msg-edited">(edited)</span>
          )}
          {/* Actions on own messages */}
          {isOwn && !msg.isDeleted && (
            <div className="batch-chat__msg-actions">
              {!hasAttachments && (
                <button className="batch-chat__msg-action-btn" onClick={() => onEdit(msg)} title="Edit">
                  <Pencil size={12} />
                </button>
              )}
              <button className="batch-chat__msg-action-btn batch-chat__msg-action-btn--danger" onClick={() => onDelete(msg._id)} title="Delete">
                <Trash2 size={12} />
              </button>
            </div>
          )}
        </div>
        <div className={`batch-chat__msg-time${isOwn ? ' batch-chat__msg-time--own' : ''}`}>
          {formatMessageTime(msg.createdAt)}
        </div>
      </div>
    </div>
  );
}

// ── MembersSidebar ──

function MembersSidebar({ members, onlineUsers, onClose }) {
  const instructors = members.filter((m) => m.role === 'instructor');
  const students = members.filter((m) => m.role === 'student');
  const onlineSet = new Set(onlineUsers);
  const onlineStudents = students.filter((m) => onlineSet.has(m._id));
  const offlineStudents = students.filter((m) => !onlineSet.has(m._id));

  const renderMember = (member, isOnline) => (
    <div key={member._id} className="batch-chat__member-item">
      <div className="batch-chat__member-avatar">
        <UserAvatar name={member.name} size="sm" src={member.profilePhoto || undefined} />
        <div className={`batch-chat__member-status-dot batch-chat__member-status-dot--${isOnline ? 'online' : 'offline'}`} />
      </div>
      <div className="batch-chat__member-info">
        <div className={`batch-chat__member-name${member.role === 'instructor' ? ' batch-chat__member-name--instructor' : ''}${!isOnline ? ' batch-chat__member-name--offline' : ''}`}>
          {member.name}
        </div>
        <div className={`batch-chat__member-status-text${isOnline ? ' batch-chat__member-status-text--online' : ''}`}>
          {isOnline ? 'Online' : 'Offline'}
        </div>
      </div>
      {member.role === 'instructor' && (
        <span className="batch-chat__msg-sender-badge">
          <GraduationCap size={10} />
        </span>
      )}
    </div>
  );

  return (
    <div className="batch-chat__members">
      <div className="batch-chat__members-header">
        <div>
          <div className="batch-chat__members-title">Members</div>
          <div className="batch-chat__members-count">{members.length} members</div>
        </div>
        <button className="batch-chat__header-btn" onClick={onClose} title="Close members">
          <X size={16} />
        </button>
      </div>
      <div className="batch-chat__members-list">
        {instructors.length > 0 && (
          <div className="batch-chat__members-group">
            <div className="batch-chat__members-group-label">Instructor</div>
            {instructors.map((m) => renderMember(m, onlineSet.has(m._id)))}
          </div>
        )}
        {onlineStudents.length > 0 && (
          <div className="batch-chat__members-group">
            <div className="batch-chat__members-group-label">Online — {onlineStudents.length}</div>
            {onlineStudents.map((m) => renderMember(m, true))}
          </div>
        )}
        {offlineStudents.length > 0 && (
          <div className="batch-chat__members-group">
            <div className="batch-chat__members-group-label">Offline — {offlineStudents.length}</div>
            {offlineStudents.map((m) => renderMember(m, false))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main BatchChat Component ──

export default function BatchChat() {
  const user = getCurrentUser();
  const { socket, connected, clearUnreadChat } = useSocket();

  const [batches, setBatches] = useState([]);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [batchSearch, setBatchSearch] = useState('');
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [editingMsg, setEditingMsg] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const prevBatchRef = useRef(null);

  const currentUserId = user?._id || user?.id;

  // ── Clear unread chat badge when chat page is opened ──
  useEffect(() => {
    clearUnreadChat();
  }, [clearUnreadChat]);

  // ── Fetch batches ──
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const { data } = await apiClient.get('/chat/batches');
        setBatches(data);
        // Auto-select first batch if none active
        if (data.length > 0 && !activeBatchId) {
          setActiveBatchId(data[0]._id);
        }
      } catch (err) {
        console.error('[Chat] Failed to fetch batches:', err);
      }
    };
    fetchBatches();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch messages when batch changes ──
  const fetchMessages = useCallback(async (batchId, before = null) => {
    if (!batchId) return;
    setLoadingMessages(true);
    try {
      const params = before ? `?before=${before}&limit=50` : '?limit=50';
      const { data } = await apiClient.get(`/chat/${batchId}/messages${params}`);
      if (before) {
        setMessages((prev) => [...data.messages, ...prev]);
      } else {
        setMessages(data.messages);
      }
      setHasMore(data.hasMore);
    } catch (err) {
      console.error('[Chat] Failed to fetch messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // ── Fetch members ──
  const fetchMembers = useCallback(async (batchId) => {
    if (!batchId) return;
    try {
      const { data } = await apiClient.get(`/chat/${batchId}/members`);
      setMembers(data);
    } catch (err) {
      console.error('[Chat] Failed to fetch members:', err);
    }
  }, []);

  // ── Handle batch change ──
  useEffect(() => {
    if (!activeBatchId) return;

    // Leave previous batch chat room
    if (prevBatchRef.current && socket) {
      socket.emit('chat:leave', { batchId: prevBatchRef.current });
    }

    // Reset state
    setMessages([]);
    setMembers([]);
    setOnlineUsers([]);
    setEditingMsg(null);
    setTypingUsers([]);
    setMessageInput('');

    // Fetch data
    fetchMessages(activeBatchId);
    fetchMembers(activeBatchId);

    // Join new batch chat room
    if (socket && connected) {
      socket.emit('chat:join', { batchId: activeBatchId });
    }

    prevBatchRef.current = activeBatchId;
    setShowMobileSidebar(false);
  }, [activeBatchId, socket, connected, fetchMessages, fetchMembers]);

  // ── Socket event listeners ──
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (msg) => {
      if (msg.batch === activeBatchId || msg.batch?._id === activeBatchId) {
        setMessages((prev) => [...prev, msg]);
      }
      // Update last message and move batch to top of list (like WhatsApp)
      setBatches((prev) => {
        const batchId = msg.batch?._id || msg.batch;
        const updated = prev.map((b) =>
          b._id === batchId
            ? { ...b, lastMessage: { message: msg.message, senderName: msg.senderName, createdAt: msg.createdAt } }
            : b
        );
        const idx = updated.findIndex((b) => b._id === batchId);
        if (idx > 0) {
          const [batch] = updated.splice(idx, 1);
          updated.unshift(batch);
        }
        return updated;
      });
    };

    const handleMessageEdited = (msg) => {
      setMessages((prev) =>
        prev.map((m) => (m._id === msg._id ? { ...m, message: msg.message, isEdited: true, editedAt: msg.editedAt } : m))
      );
    };

    const handleMessageDeleted = ({ _id }) => {
      setMessages((prev) =>
        prev.map((m) => (m._id === _id ? { ...m, isDeleted: true, message: 'This message was deleted' } : m))
      );
    };

    const handleOnline = ({ batchId, onlineUsers: users }) => {
      if (batchId === activeBatchId) {
        setOnlineUsers(users);
      }
      // Update online count in batch list
      setBatches((prev) =>
        prev.map((b) => (b._id === batchId ? { ...b, onlineCount: users.length } : b))
      );
    };

    const handleTyping = ({ userId, userName, isTyping }) => {
      if (userId === currentUserId) return;
      setTypingUsers((prev) => {
        if (isTyping) {
          return prev.some((t) => t.userId === userId) ? prev : [...prev, { userId, userName }];
        }
        return prev.filter((t) => t.userId !== userId);
      });
    };

    socket.on('chat:newMessage', handleNewMessage);
    socket.on('chat:messageEdited', handleMessageEdited);
    socket.on('chat:messageDeleted', handleMessageDeleted);
    socket.on('chat:online', handleOnline);
    socket.on('chat:userTyping', handleTyping);

    return () => {
      socket.off('chat:newMessage', handleNewMessage);
      socket.off('chat:messageEdited', handleMessageEdited);
      socket.off('chat:messageDeleted', handleMessageDeleted);
      socket.off('chat:online', handleOnline);
      socket.off('chat:userTyping', handleTyping);
    };
  }, [socket, activeBatchId, currentUserId]);

  // ── Auto-scroll to bottom ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Load more (scroll to top) ──
  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el || !hasMore || loadingMessages) return;
    if (el.scrollTop < 60 && messages.length > 0) {
      fetchMessages(activeBatchId, messages[0].createdAt);
    }
  }, [hasMore, loadingMessages, messages, activeBatchId, fetchMessages]);

  // ── Upload files and send message ──
  const uploadFiles = useCallback(async (files) => {
    const uploaded = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      uploaded.push({
        fileName: file.name,
        fileKey: data.key,
        fileSize: file.size,
        mimeType: file.type,
      });
    }
    return uploaded;
  }, []);

  // ── Send message ──
  const handleSend = useCallback(async () => {
    if ((!messageInput.trim() && pendingFiles.length === 0) || !socket || !activeBatchId) return;
    if (uploading) return;

    try {
      let attachments = [];
      if (pendingFiles.length > 0) {
        setUploading(true);
        attachments = await uploadFiles(pendingFiles);
        setUploading(false);
      }

      if (editingMsg) {
        socket.emit('chat:editMessage', {
          messageId: editingMsg._id,
          message: messageInput.trim(),
        });
        setEditingMsg(null);
      } else {
        socket.emit('chat:sendMessage', {
          batchId: activeBatchId,
          message: messageInput.trim(),
          attachments,
        });
      }

      setMessageInput('');
      setPendingFiles([]);
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.focus();
      }

      // Clear typing
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      socket.emit('chat:typing', { batchId: activeBatchId, isTyping: false });
    } catch (err) {
      console.error('[Chat] Failed to send:', err);
      setUploading(false);
    }
  }, [messageInput, pendingFiles, socket, activeBatchId, editingMsg, uploading, uploadFiles]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Auto-resize textarea ──
  const autoResize = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 138)}px`;
  }, []);

  // ── Typing indicator ──
  const handleInputChange = (e) => {
    setMessageInput(e.target.value);
    autoResize();
    if (!socket || !activeBatchId) return;

    socket.emit('chat:typing', { batchId: activeBatchId, isTyping: true });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('chat:typing', { batchId: activeBatchId, isTyping: false });
    }, 2000);
  };

  // ── Edit / Delete ──
  const handleEdit = (msg) => {
    setEditingMsg(msg);
    setMessageInput(msg.message);
    inputRef.current?.focus();
  };

  const handleCancelEdit = () => {
    setEditingMsg(null);
    setMessageInput('');
    setPendingFiles([]);
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  // ── File selection ──
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Max 5 files at a time, 25MB each
    const valid = files.filter(f => f.size <= 25 * 1024 * 1024).slice(0, 5);
    setPendingFiles((prev) => [...prev, ...valid].slice(0, 5));
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDelete = (messageId) => {
    if (!socket) return;
    socket.emit('chat:deleteMessage', { messageId });
  };

  // ── Select batch helper ──
  const handleSelectBatch = (batchId) => {
    setActiveBatchId(batchId);
  };

  // ── Active batch info ──
  const activeBatch = batches.find((b) => b._id === activeBatchId);
  const onlineCount = onlineUsers.length;

  // ── Typing text ──
  const typingText = useMemo(() => {
    if (typingUsers.length === 0) return '';
    if (typingUsers.length === 1) return `${typingUsers[0].userName?.split(' ')[0]} is typing...`;
    if (typingUsers.length === 2) return `${typingUsers[0].userName?.split(' ')[0]} and ${typingUsers[1].userName?.split(' ')[0]} are typing...`;
    return 'Several people are typing...';
  }, [typingUsers]);

  // ── Render messages with date separators ──
  let lastDate = null;

  return (
    <div className="batch-chat">
      {/* Desktop Sidebar */}
      <BatchListSidebar
        batches={batches}
        activeBatchId={activeBatchId}
        onSelectBatch={handleSelectBatch}
        searchQuery={batchSearch}
        onSearchChange={setBatchSearch}
      />

      {/* Mobile Sidebar */}
      <div
        className={`batch-chat__mobile-overlay${showMobileSidebar ? ' batch-chat__mobile-overlay--visible' : ''}`}
        onClick={() => setShowMobileSidebar(false)}
      />
      <div className={`batch-chat__mobile-sidebar${showMobileSidebar ? ' batch-chat__mobile-sidebar--visible' : ''}`}>
        <BatchListSidebar
          batches={batches}
          activeBatchId={activeBatchId}
          onSelectBatch={handleSelectBatch}
          searchQuery={batchSearch}
          onSearchChange={setBatchSearch}
        />
      </div>

      {/* Main Chat */}
      <div className="batch-chat__main">
        {!activeBatchId ? (
          <div className="batch-chat__no-batch">
            <MessageCircle />
            <p>Select a batch to start chatting</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="batch-chat__header">
              <div className="batch-chat__header-left">
                <button
                  className="batch-chat__header-back"
                  onClick={() => setShowMobileSidebar(true)}
                >
                  <ArrowLeft size={16} />
                </button>
                <div className="batch-chat__header-icon">
                  <Hash size={16} />
                </div>
                <div className="batch-chat__header-info">
                  <div className="batch-chat__header-name">
                    {activeBatch?.name || 'Loading...'}
                  </div>
                  <div className="batch-chat__header-sub">
                    <span>{activeBatch?.courseName}</span>
                    {onlineCount > 0 && (
                      <span className="batch-chat__header-online">
                        <span className="dot" />
                        {onlineCount} online
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="batch-chat__header-actions">
                <button
                  className={`batch-chat__header-btn${showMembers ? ' batch-chat__header-btn--active' : ''}`}
                  onClick={() => setShowMembers(!showMembers)}
                  title="Toggle members"
                >
                  <Users size={16} />
                </button>
              </div>
            </div>

            {/* Chat Content */}
            <div className="batch-chat__content">
              <div className="batch-chat__messages-area">
                <div
                  className="batch-chat__messages"
                  ref={messagesContainerRef}
                  onScroll={handleScroll}
                >
                  {loadingMessages && messages.length === 0 ? (
                    <div className="batch-chat__loading">Loading messages...</div>
                  ) : messages.length === 0 ? (
                    <div className="batch-chat__empty">
                      <div className="batch-chat__empty-icon">
                        <MessageCircle size={32} />
                      </div>
                      <div className="batch-chat__empty-title">No messages yet</div>
                      <p className="batch-chat__empty-text">
                        Be the first to send a message in this batch chat
                      </p>
                    </div>
                  ) : (
                    <>
                      {hasMore && (
                        <div className="batch-chat__loading">
                          {loadingMessages ? 'Loading older messages...' : ''}
                        </div>
                      )}
                      {messages.map((msg, idx) => {
                        const msgDate = getDateLabel(msg.createdAt);
                        const showDate = msgDate !== lastDate;
                        lastDate = msgDate;
                        const isOwn = msg.sender === currentUserId;
                        const previousSenderId = idx > 0 ? messages[idx - 1].sender : null;

                        return (
                          <div key={msg._id || idx}>
                            {showDate && (
                              <div className="batch-chat__date-sep">
                                <div className="batch-chat__date-label">{msgDate}</div>
                              </div>
                            )}
                            <MessageBubble
                              msg={msg}
                              isOwn={isOwn}
                              previousSenderId={previousSenderId}
                              onEdit={handleEdit}
                              onDelete={handleDelete}
                            />
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Typing indicator */}
                <div className="batch-chat__typing">{typingText}</div>

                {/* Input Area */}
                <div className="batch-chat__input-area">
                  {editingMsg && (
                    <div className="batch-chat__edit-bar">
                      <span className="batch-chat__edit-bar-label">Editing</span>
                      <span className="batch-chat__edit-bar-text">{editingMsg.message}</span>
                      <button className="batch-chat__edit-bar-close" onClick={handleCancelEdit}>
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {/* Pending files preview */}
                  {pendingFiles.length > 0 && (
                    <div className="batch-chat__pending-files">
                      {pendingFiles.map((file, i) => (
                        <div key={i} className="batch-chat__pending-file">
                          {file.type?.startsWith('image/') ? <ImageIcon size={14} /> : <FileText size={14} />}
                          <span className="batch-chat__pending-file-name">{file.name}</span>
                          <button className="batch-chat__pending-file-remove" onClick={() => removePendingFile(i)}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="batch-chat__input-row">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="d-none"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                      onChange={handleFileSelect}
                    />
                    <button
                      className="batch-chat__attach-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || !!editingMsg}
                      title="Attach files"
                    >
                      <Paperclip size={18} />
                    </button>
                    <div className="batch-chat__input-wrap">
                      <textarea
                        ref={inputRef}
                        className="batch-chat__input"
                        placeholder="Type a message..."
                        value={messageInput}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        rows={1}
                      />
                    </div>
                    <button
                      className="batch-chat__send-btn"
                      onClick={handleSend}
                      disabled={(!messageInput.trim() && pendingFiles.length === 0) || uploading}
                      title="Send message"
                    >
                      {uploading ? <Loader size={16} className="spin" /> : <Send size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Members Sidebar */}
              {showMembers && (
                <MembersSidebar
                  members={members}
                  onlineUsers={onlineUsers}
                  onClose={() => setShowMembers(false)}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
